// UX76 (docs/build-briefs/UX76-reports-full-width.md): a table wrapper that
// still scrolls sideways says so. Each wrapper gets data-scroll-cue ("right",
// "left" or "both", absent when nothing is hidden) from its real overflow, and
// a visible cue line above it. CSS draws a fade on the hidden edge. The cue is
// kept current as the report redraws, the page resizes and the wrapper scrolls.
// Scroll, resize and DOM-change observers only: no key or hover handler.

const WRAPPER_SELECTOR = ".tblwrap, .hscroll";
const CUE_TEXT = "Scroll sideways for more columns";
/** Overflow smaller than this is rounding, not a hidden column. */
const SLACK_PX = 2;

function cueFor(wrap: HTMLElement): "right" | "left" | "both" | "" {
  const hidden = wrap.scrollWidth - wrap.clientWidth;
  if (hidden <= SLACK_PX) return "";
  const more = wrap.scrollLeft < hidden - SLACK_PX;
  const behind = wrap.scrollLeft > SLACK_PX;
  if (more && behind) return "both";
  return more ? "right" : "left";
}

function cueLineOf(wrap: HTMLElement): HTMLElement | null {
  const previous = wrap.previousElementSibling;
  return previous instanceof HTMLElement && previous.classList.contains("scroll-cue")
    ? previous
    : null;
}

/** Brings every wrapper under `host` in line with its real overflow. */
export function updateScrollCues(host: HTMLElement): void {
  for (const wrap of host.querySelectorAll<HTMLElement>(WRAPPER_SELECTOR)) {
    const cue = cueFor(wrap);
    if (cue === "") {
      wrap.removeAttribute("data-scroll-cue");
    } else if (wrap.getAttribute("data-scroll-cue") !== cue) {
      wrap.setAttribute("data-scroll-cue", cue);
    }
    const scrollable = wrap.scrollWidth - wrap.clientWidth > SLACK_PX;
    const line = cueLineOf(wrap);
    if (scrollable && line === null) {
      const created = document.createElement("p");
      created.className = "scroll-cue";
      created.dataset["testid"] = "scroll-cue";
      created.textContent = `${CUE_TEXT} ›`;
      wrap.before(created);
    } else if (!scrollable && line !== null) {
      line.remove();
    }
  }
}

/** Keeps the cues current for everything drawn inside `host`. */
export function installScrollCues(host: HTMLElement): void {
  let queued = false;
  const run = (): void => {
    queued = false;
    // Our own inserts and removals must not wake the observer again.
    observer.disconnect();
    updateScrollCues(host);
    observer.observe(host, { childList: true, subtree: true });
  };
  const schedule = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  };
  const observer = new MutationObserver(schedule);
  observer.observe(host, { childList: true, subtree: true });
  window.addEventListener("resize", schedule);
  // A scroll event does not bubble, so listen in the capture phase.
  host.addEventListener("scroll", schedule, true);
  schedule();
}
